import { supabase } from '@/integrations/supabase/client';

export interface TrendingTopic {
  topic: string;
  connections: number;
  trend: 'up' | 'down' | 'stable';
  change: string;
  description: string;
}

export interface TimeSlotActivity {
  period: string;
  activity: string;
  connections: number;
  color: string;
}

export interface RealEvent {
  id: string;
  title: string;
  start_date: string;
  end_date?: string;
  capacity?: number;
  registrations: number;
  location?: string;
  created_by?: string;
}

export interface NetworkingGroup {
  name: string;
  members: number;
  topic: string;
  activity: string;
  lastActivity: string;
  color: string;
}

export const fetchTrendingTopics = async (): Promise<TrendingTopic[]> => {
  try {
    // Analyser les skills et needs les plus populaires
    const { data: members, error } = await supabase
      .from('club_members')
      .select('skills, needs, tags, industry')
      .eq('status', 'active');

    if (error) throw error;

    // Compter les occurrences de skills et needs
    const topicCounts: { [key: string]: number } = {};
    
    members?.forEach(member => {
      // Compter les skills
      member.skills?.forEach((skill: string) => {
        topicCounts[skill] = (topicCounts[skill] || 0) + 1;
      });
      
      // Compter les needs
      member.needs?.forEach((need: string) => {
        topicCounts[need] = (topicCounts[need] || 0) + 1;
      });
      
      // Compter les tags
      member.tags?.forEach((tag: string) => {
        topicCounts[tag] = (topicCounts[tag] || 0) + 1;
      });
    });

    // Trier par popularité et prendre le top 4
    const sortedTopics = Object.entries(topicCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 4);

    const trendingTopicsWithChanges = await Promise.all(
      sortedTopics.map(async ([topic, count], index) => ({
        topic,
        connections: count,
        trend: calculateRealTrend(topic, count, members),
        change: await calculateRealTrendChange(topic),
        description: generateTopicDescription(topic, count)
      }))
    );

    return trendingTopicsWithChanges;
  } catch (error) {
    console.error('Error fetching trending topics:', error);
    return [];
  }
};

export const fetchTimeSlotActivity = async (): Promise<TimeSlotActivity[]> => {
  try {
    // Analyser les créneaux d'activité basés sur les événements et connexions
    const { data: events, error } = await supabase
      .from('events')
      .select('start_date');

    if (error) throw error;

    const timeSlots = [
      { period: '9h-12h', hour: 9 },
      { period: '12h-14h', hour: 12 },
      { period: '14h-18h', hour: 14 },
      { period: '18h-21h', hour: 18 }
    ];

    // Compter les événements par créneau
    const slotCounts = await Promise.all(timeSlots.map(async slot => {
      const count = events?.filter(event => {
        const eventHour = new Date(event.start_date).getHours();
        return eventHour >= slot.hour && eventHour < (slot.hour === 18 ? 21 : slot.hour + 3);
      }).length || 0;

      return {
        period: slot.period,
        activity: count > 2 ? 'Très haute' : count > 1 ? 'Haute' : 'Moyenne',
        connections: await getActualConnections(slot.period),
        color: count > 2 ? 'bg-green-100 text-green-800' : 
               count > 1 ? 'bg-yellow-100 text-yellow-800' : 
               'bg-gray-100 text-gray-800'
      };
    }));

    return slotCounts;
  } catch (error) {
    console.error('Error fetching time slot activity:', error);
    return [];
  }
};

export const fetchUpcomingEvents = async (): Promise<RealEvent[]> => {
  try {
    const { data: events, error } = await supabase
      .from('events')
      .select(`
        id,
        title,
        start_date,
        end_date,
        capacity,
        location,
        created_by,
        event_registrations(count)
      `)
      .gte('start_date', new Date().toISOString())
      .order('start_date', { ascending: true })
      .limit(10);

    if (error) throw error;

    return events?.map(event => ({
      id: event.id,
      title: event.title,
      start_date: event.start_date,
      end_date: event.end_date,
      capacity: event.capacity,
      registrations: Array.isArray(event.event_registrations) ? event.event_registrations.length : 0,
      location: event.location,
      created_by: event.created_by
    })) || [];
  } catch (error) {
    console.error('Error fetching upcoming events:', error);
    return [];
  }
};

export const fetchNetworkingGroups = async (): Promise<NetworkingGroup[]> => {
  try {
    // Analyser les groupes basés sur les industries et tags communes
    const { data: members, error } = await supabase
      .from('club_members')
      .select('industry, tags, status')
      .eq('status', 'active');

    if (error) throw error;

    const industryGroups: { [key: string]: number } = {};
    const tagGroups: { [key: string]: number } = {};

    members?.forEach(member => {
      if (member.industry) {
        industryGroups[member.industry] = (industryGroups[member.industry] || 0) + 1;
      }
      
      member.tags?.forEach((tag: string) => {
        tagGroups[tag] = (tagGroups[tag] || 0) + 1;
      });
    });

    // Combiner les groupes par industrie et tags
    const allGroups = [
      ...Object.entries(industryGroups).map(([name, count]) => ({
        name: `Groupe ${name}`,
        members: count,
        topic: name,
        activity: count > 3 ? 'Très active' : count > 1 ? 'Active' : 'Modérée',
        lastActivity: count > 3 ? '2h' : count > 1 ? '1j' : '3j',
        color: getTopicColor(name)
      })),
      ...Object.entries(tagGroups)
        .filter(([, count]) => count > 1)
        .slice(0, 3)
        .map(([name, count]) => ({
          name: `Communauté ${name}`,
          members: count,
          topic: name,
          activity: count > 3 ? 'Très active' : 'Active',
          lastActivity: count > 3 ? '1h' : '2j',
          color: getTopicColor(name)
        }))
    ];

    return allGroups.slice(0, 6); // Limiter à 6 groupes
  } catch (error) {
    console.error('Error fetching networking groups:', error);
    return [];
  }
};

const generateTopicDescription = (topic: string, count: number): string => {
  const descriptions = {
    'default': `${count} membres actifs recherchent des connexions autour de ce sujet`
  };
  
  return descriptions[topic] || descriptions['default'];
};

const calculateRealTrend = (topic: string, currentCount: number, members: any[]): 'up' | 'down' | 'stable' => {
  // Calculer la tendance basée sur les créations récentes de membres avec ce skill/need
  const recentMembers = members.filter(m => {
    const join_date = new Date(m.created_at || m.join_date);
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return join_date > monthAgo && (
      m.skills?.includes(topic) || 
      m.needs?.includes(topic) || 
      m.tags?.includes(topic)
    );
  });
  
  const recentCount = recentMembers.length;
  const totalWithTopic = currentCount;
  const growthRate = totalWithTopic > 0 ? recentCount / totalWithTopic : 0;
  
  if (growthRate > 0.3) return 'up';
  if (growthRate < 0.1) return 'down';
  return 'stable';
};

const calculateRealTrendChange = async (topic: string): Promise<string> => {
  try {
    // Comparer les données du mois dernier vs mois précédent
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    
    const { data: lastMonthMembers } = await supabase
      .from('club_members')
      .select('skills, needs, tags')
      .gte('created_at', lastMonth.toISOString())
      .lt('created_at', now.toISOString());
    
    const { data: previousMonthMembers } = await supabase
      .from('club_members')
      .select('skills, needs, tags')
      .gte('created_at', twoMonthsAgo.toISOString())
      .lt('created_at', lastMonth.toISOString());
    
    const lastMonthCount = (lastMonthMembers || []).filter(m => 
      m.skills?.includes(topic) || m.needs?.includes(topic) || m.tags?.includes(topic)
    ).length;
    
    const previousMonthCount = (previousMonthMembers || []).filter(m => 
      m.skills?.includes(topic) || m.needs?.includes(topic) || m.tags?.includes(topic)
    ).length;
    
    if (previousMonthCount === 0) return '+100%';
    
    const change = ((lastMonthCount - previousMonthCount) / previousMonthCount) * 100;
    return change > 0 ? `+${Math.round(change)}%` : `${Math.round(change)}%`;
  } catch (error) {
    console.error('Error calculating trend change:', error);
    return '0%';
  }
};

const getActualConnections = async (period: string): Promise<number> => {
  try {
    // Compter les vraies connexions établies pendant ce créneau
    const { data: connections } = await supabase
      .from('connection_requests')
      .select('created_at')
      .eq('status', 'accepted');
    
    if (!connections) return 0;
    
    const [startHour, endHour] = period.split('-').map(time => {
      const hour = parseInt(time.replace('h', ''));
      return hour;
    });
    
    const connectionsInSlot = connections.filter(conn => {
      const hour = new Date(conn.created_at).getHours();
      return hour >= startHour && hour < endHour;
    });
    
    return connectionsInSlot.length;
  } catch (error) {
    console.error('Error getting actual connections:', error);
    return 0;
  }
};

const getTopicColor = (topic: string): string => {
  const colors = [
    'bg-blue-100 text-blue-800',
    'bg-green-100 text-green-800',
    'bg-purple-100 text-purple-800',
    'bg-orange-100 text-orange-800',
    'bg-pink-100 text-pink-800',
    'bg-indigo-100 text-indigo-800'
  ];
  
  // Hash simple pour assigner une couleur consistante
  const hash = topic.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  
  return colors[Math.abs(hash) % colors.length];
};